import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { FileDown, Loader2 } from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import pptxgen from "pptxgenjs";
import type { Slide } from "./PresentationTemplates";

interface ExportButtonsProps {
  deckName: string;
  slides: Slide[];
}

interface Branding {
  logo_url: string | null;
  primary_color: string;
  company_name: string;
  font_heading: string | null;
  font_body: string | null;
}

// Loads a Google Font by family name via a <link> tag (idempotent - reuses
// an existing link if already injected) and resolves once the browser has
// actually finished loading it, so html2canvas doesn't silently rasterize
// a fallback font before the real one arrives. Falls back to resolving
// immediately for names that aren't real Google Fonts (e.g. "Inter" is
// already loaded some other way, or a custom/system name was typed in).
function ensureGoogleFontLoaded(family: string): Promise<void> {
  if (!family || family === "Inter") return Promise.resolve();
  const linkId = `google-font-${family.replace(/\s+/g, "-").toLowerCase()}`;
  if (!document.getElementById(linkId)) {
    const link = document.createElement("link");
    link.id = linkId;
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family)}:wght@400;700&display=swap`;
    document.head.appendChild(link);
  }
  if ("fonts" in document) {
    return document.fonts.load(`700 32px "${family}"`)
      .then(() => document.fonts.load(`400 16px "${family}"`))
      .then(() => undefined)
      .catch(() => undefined);
  }
  return Promise.resolve();
}

// pptxgenjs's SVG embedding requires it to detect the SVG mime type from
// a fetched response and dual-embed both a PNG fallback and the SVG
// itself - fragile in practice (silently dropped the logo entirely when
// tested against a real uploaded .svg, no error). Browsers render SVG
// <img> tags natively, so the PDF export (html2canvas) already works
// for any format; this rasterizes the logo to PNG once via an offscreen
// canvas so the PPTX path gets a plain PNG regardless of whether the
// original upload was PNG, JPEG, or SVG.
function logoUrlToPngDataUrl(url: string): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth || 128;
      canvas.height = img.naturalHeight || 128;
      const ctx = canvas.getContext("2d");
      if (!ctx) return resolve(null);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      try {
        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

export function ExportButtons({ deckName, slides }: ExportButtonsProps) {
  const { toast } = useToast();
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [isExportingPptx, setIsExportingPptx] = useState(false);

  const { data: branding } = useQuery({
    queryKey: ["company-branding-for-export"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("company_branding")
        .select("logo_url, primary_color, company_name, font_heading, font_body")
        .maybeSingle();
      if (error) throw error;
      return data as Branding | null;
    },
  });

  const primaryColor = branding?.primary_color ?? "#6366f1";
  const companyName = branding?.company_name ?? "My Company";
  const logoUrl = branding?.logo_url ?? null;
  const fontHeading = branding?.font_heading ?? "Inter";
  const fontBody = branding?.font_body ?? "Inter";

  const handleExportPdf = async () => {
    setIsExportingPdf(true);
    try {
      await Promise.all([ensureGoogleFontLoaded(fontHeading), ensureGoogleFontLoaded(fontBody)]);
      const pdf = new jsPDF("landscape", "px", [1280, 720]);
      for (let i = 0; i < slides.length; i++) {
        const el = slideRefs.current[i];
        if (!el) continue;
        const canvas = await html2canvas(el, { scale: 2, useCORS: true });
        const imgData = canvas.toDataURL("image/png");
        if (i > 0) pdf.addPage([1280, 720], "landscape");
        pdf.addImage(imgData, "PNG", 0, 0, 1280, 720);
      }
      pdf.save(`${deckName.replace(/[^a-z0-9]+/gi, "-")}.pdf`);
      toast({ title: "PDF exported" });
    } catch (error) {
      toast({ title: "Export failed", description: "Please try again", variant: "destructive" });
    } finally {
      setIsExportingPdf(false);
    }
  };

  const handleExportPptx = async () => {
    setIsExportingPptx(true);
    try {
      const pptx = new pptxgen();
      pptx.defineLayout({ name: "MASIRA_16x9", width: 13.33, height: 7.5 });
      pptx.layout = "MASIRA_16x9";

      const accentHex = primaryColor.replace("#", "");
      const logoPngDataUrl = logoUrl ? await logoUrlToPngDataUrl(logoUrl) : null;

      for (const slide of slides) {
        const pptxSlide = pptx.addSlide();
        pptxSlide.background = { color: "FFFFFF" };
        pptxSlide.addShape("rect", { x: 0, y: 0, w: 0.12, h: 7.5, fill: { color: accentHex } });
        pptxSlide.addText(slide.title, {
          x: 0.6, y: 0.5, w: 12, h: 1,
          fontSize: 32, bold: true, color: "111827", fontFace: fontHeading,
        });
        pptxSlide.addText(
          slide.bullets.filter(Boolean).map((b) => ({ text: b, options: { bullet: true, breakLine: true } })),
          { x: 0.6, y: 1.8, w: 12, h: 5, fontSize: 18, color: "374151", fontFace: fontBody },
        );
        pptxSlide.addText(companyName, {
          x: logoPngDataUrl ? 1.3 : 0.6, y: 7.0, w: 6, h: 0.4, fontSize: 10, color: "9CA3AF", fontFace: fontBody,
        });
        if (logoPngDataUrl) {
          pptxSlide.addImage({ data: logoPngDataUrl, x: 0.6, y: 6.95, w: 0.5, h: 0.5 });
        }
        if (slide.notes) {
          pptxSlide.addNotes(slide.notes);
        }
      }

      await pptx.writeFile({ fileName: `${deckName.replace(/[^a-z0-9]+/gi, "-")}.pptx` });
      toast({ title: "PPTX exported" });
    } catch (error) {
      toast({ title: "Export failed", description: "Please try again", variant: "destructive" });
    } finally {
      setIsExportingPptx(false);
    }
  };

  return (
    <>
      <Button variant="outline" onClick={handleExportPptx} disabled={isExportingPptx}>
        {isExportingPptx ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : <FileDown className="h-4 w-4 me-2" />}
        Export PPTX
      </Button>

      <Button variant="outline" onClick={handleExportPdf} disabled={isExportingPdf}>
        {isExportingPdf ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : <FileDown className="h-4 w-4 me-2" />}
        Export PDF
      </Button>

      {/* Off-screen render targets for PDF export - fixed 1280x720 (16:9) so
          html2canvas output matches the jsPDF page size exactly. */}
      <div style={{ position: "fixed", left: "-9999px", top: 0 }}>
        {slides.map((slide, i) => (
          <div
            key={i}
            ref={(el) => (slideRefs.current[i] = el)}
            style={{
              width: 1280,
              height: 720,
              background: "#ffffff",
              padding: 64,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              fontFamily: `"${fontBody}", sans-serif`,
            }}
          >
            <div style={{ borderLeft: `6px solid ${primaryColor}`, paddingLeft: 32 }}>
              <h1 style={{ fontFamily: `"${fontHeading}", sans-serif`, fontSize: 40, fontWeight: 700, color: "#111827", marginBottom: 24 }}>
                {slide.title}
              </h1>
              <ul style={{ fontSize: 22, color: "#374151", lineHeight: 1.8, listStyle: "disc", paddingLeft: 24 }}>
                {slide.bullets.filter(Boolean).map((bullet, bi) => (
                  <li key={bi}>{bullet}</li>
                ))}
              </ul>
            </div>
            <div style={{ position: "absolute", bottom: 32, right: 64, display: "flex", alignItems: "center", gap: 12 }}>
              {logoUrl && (
                <img src={logoUrl} alt="" crossOrigin="anonymous" style={{ height: 28, width: "auto" }} />
              )}
              <span style={{ fontSize: 14, color: "#9ca3af" }}>{companyName}</span>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
